// AssetsAtLocationGrid.tsx
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CellClickedEvent,
  GridApi,
  GridOptions,
  ICellRendererParams,
  RowDoubleClickedEvent,
  SortChangedEvent,
  GridReadyEvent,
  isColumnSelectionCol,
} from 'ag-grid-community';
import { checkboxSelectionColumnDef } from '@/lib/ag-grid/selection';
import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import LinkCellRenderer, { LinkCellRendererParams } from '@/components/grid/cells/LinkCellRenderer';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { ActionBar } from '@/modules/application';
import { Button } from '@mui/material';
import { ColumnID } from '@/components/grid/column-ids';
import { PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { AssetSortOption } from '../../api/assets/asset.contracts';
import { MoveAssetsToLocationModal } from '@/modules/locations/components/MoveAssetsToLocationModal';
import { useTranslation } from '@/lib';
import { LocationAssetModel } from '@/modules/locations/types/LocationAssetModel';
import ChangeAssetTypeModal from '@/modules/locations/components/ChangeAssetTypeModal';

interface AssetsAtLocationGridProps {
  data?: LocationAssetModel[];
  isError: boolean;
  noBorder?: boolean;
  isLoading?: boolean;
  onAssetsMoved: () => void;
  onSortChange: (sortOption?: PageSortOption, sortDirection?: PageSortOrder) => void;
  selectedRows: LocationAssetModel[];
  onSelectionChanged: (selectedRows: LocationAssetModel[]) => void;
  sortOption?: PageSortOption;
  sortDirection?: PageSortOrder;
  setGridApi?: (api: GridApi<LocationAssetModel>) => void;
}

export const AssetsAtLocationGrid: FunctionComponent<AssetsAtLocationGridProps> = ({
  data,
  isError,
  noBorder,
  isLoading,
  onAssetsMoved,
  onSortChange,
  selectedRows,
  onSelectionChanged,
  sortOption,
  sortDirection,
  setGridApi,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMoveAssetModalOpen, setIsMoveAssetModalOpen] = useState(false);
  const [isChangeAssetTypeModalOpen, setIsChangeAssetTypeModalOpen] = useState(false);
  const [gridApi, setInternalGridApi] = useState<GridApi<LocationAssetModel> | null>(null);

  const onCellClicked = useCallback((params: CellClickedEvent<LocationAssetModel>) => {
    if (isColumnSelectionCol(params.column)) {
      const node = params.node;
      node.setSelected(!node.isSelected());
    }
  }, []);

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<LocationAssetModel>) => {
      if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
        const changedColumn = event.columns.find((col) => col.getSort());
        if (!changedColumn) {
          onSortChange();
          return;
        }
        const changedColumnId = changedColumn.getColId();

        let sortOption: AssetSortOption | undefined = undefined;

        switch (changedColumnId) {
          case ColumnID.ASSET_CODE:
            sortOption = AssetSortOption.ASSET_CODE;
            break;
          case ColumnID.ASSET_TYPE_NAME:
            sortOption = AssetSortOption.ASSET_TYPE_NAME;
            break;
          case ColumnID.DATE_ARRIVED_AT_LOCATION:
            sortOption = AssetSortOption.LOCATION_ENTER_DATE;
            break;
          default:
            break;
        }

        let sortOrder: PageSortOrder | undefined = undefined;

        switch (changedColumn.getSort()) {
          case 'asc':
            sortOrder = PageSortOrder.ASC;
            break;
          case 'desc':
            sortOrder = PageSortOrder.DESC;
            break;
        }

        onSortChange(sortOption, sortOrder);
      }
    },
    [onSortChange],
  );

  const customGridOptions: GridOptions<LocationAssetModel> = {
    ...BASE_GRID_OPTIONS,
    getRowId: (params) => params.data.dto.assetId.toString(),
    onRowDoubleClicked(event: RowDoubleClickedEvent<LocationAssetModel>) {
      navigate(`/app/assets/${event.data?.dto.assetId}`);
    },
    rowSelection: { mode: 'multiRow' },
    selectionColumnDef: checkboxSelectionColumnDef(),
    onCellClicked: onCellClicked,
    onSelectionChanged: (event) => {
      onSelectionChanged(event.api.getSelectedRows());
    },
    onGridReady: (event: GridReadyEvent<LocationAssetModel>) => {
      setInternalGridApi(event.api);
      setGridApi?.(event.api);

      // Set initial sort state to grid
      if (sortOption && sortDirection) {
        // Convert sort option to column id
        let columnId: ColumnID | undefined;
        switch (sortOption) {
          case AssetSortOption.LOCATION_ENTER_DATE:
            columnId = ColumnID.DATE_ARRIVED_AT_LOCATION;
            break;
          case AssetSortOption.ASSET_TYPE_NAME:
            columnId = ColumnID.ASSET_TYPE_NAME;
            break;
          case AssetSortOption.ASSET_CODE:
            columnId = ColumnID.ASSET_CODE;
            break;
          default:
            break;
        }

        if (columnId) {
          event.api.applyColumnState({
            state: [
              {
                colId: columnId,
                sort: sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
              },
            ],
            defaultState: { sort: null },
          });
        }
      }
    },
    onSortChanged: handleSortChanged,
  };

  const columns: ColDefOrGroup<LocationAssetModel>[] = [
    {
      colId: ColumnID.ASSET_CODE,
      field: 'dto.assetCode',
      headerName: t('code'),
      cellRenderer: LinkCellRenderer,
      cellRendererParams: (params: ICellRendererParams<LocationAssetModel>): LinkCellRendererParams => ({
        pathname: params.data?.dto.assetId ? `/app/assets/${params.data?.dto.assetId}` : undefined,
      }),
    },
    {
      colId: ColumnID.ASSET_TYPE_NAME,
      field: 'dto.assetTypeName',
      headerName: t('type'),
      // cellRenderer: LinkCellRenderer,
      // cellRendererParams: (params: ICellRendererParams<AssetModel>): LinkCellRendererParams => ({
      //   pathname: params.data?.dto.assetType.id ? `/app/configuration/assets/types/${params.data?.dto.assetType.id}` : undefined,
      // }),
    },
    {
      colId: ColumnID.DATE_ARRIVED_AT_LOCATION,
      field: 'dto.dateArrivedAtLocation',
      headerName: t('arrived_at'),
      cellRenderer: RelativeDateCellRenderer,
      flex: 1,
    },
  ];

  const onMoveSelectedAssetsClicked = () => {
    setIsMoveAssetModalOpen(true);
  };

  const onMoveSelectedAssetsClosed = () => {
    setIsMoveAssetModalOpen(false);
    clearSelection();
  };

  const onChangeAssetTypeClicked = () => {
    setIsChangeAssetTypeModalOpen(true);
  };

  const onChangeAssetTypeClosed = () => {
    setIsChangeAssetTypeModalOpen(false);
    clearSelection();
  };

  const onAssetTypeChangedByGrid = () => {
    onAssetsMoved();
  };

  const clearSelection = () => {
    if (gridApi) {
      gridApi.deselectAll();
    }
  };

  useEffect(() => {
    if (selectedRows.length === 0) {
      clearSelection();
    }
  }, [selectedRows]);

  const onAssetsMovedByGrid = () => {
    onAssetsMoved();
  };

  return (
    <div className="flex h-full flex-1 grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid
          noBorder={noBorder}
          isLoading={isLoading}
          colDefs={columns}
          rowData={data}
          gridOptions={customGridOptions}
          disableDefaultGridOptions
        />
      )}

      <ActionBar visible={selectedRows.length > 0}>
        <Button variant="outlined" color="primary" onClick={clearSelection}>
          {t('clear_selection')}
        </Button>
        <Button variant="contained" onClick={onMoveSelectedAssetsClicked}>
          {t('move_assets')}
        </Button>
        <Button variant="contained" onClick={onChangeAssetTypeClicked}>
          {t('change_asset_type')}
        </Button>
      </ActionBar>
      <MoveAssetsToLocationModal
        selectedAssetIds={selectedRows.map((asset) => asset.dto.assetId)}
        isOpen={isMoveAssetModalOpen}
        onClose={onMoveSelectedAssetsClosed}
        onAssetsMoved={onAssetsMovedByGrid}
      />
      <ChangeAssetTypeModal
        selectedAssetIds={selectedRows.map((asset) => asset.dto.assetId)}
        isOpen={isChangeAssetTypeModalOpen}
        onClose={onChangeAssetTypeClosed}
        onAssetsMoved={onAssetTypeChangedByGrid}
      />
    </div>
  );
};
